import { IExchangeRatePair, IExchangeRatesMapResponse } from "@finbackoffice/clientbff-client";
import { Currency } from "@finbackoffice/enums";
import {
    FC,
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { useRuntimeConfig } from "@finbackoffice/site-core";
import { WalletsContext } from "./wallets.provider";
import { UserAccountContextV2 } from "./user-account-v2.provider";
import { ConfigContextV2 } from "./config-v2.provider";

function roundTo(num: number, decimals: number) {
    const factor = 10 ** decimals;
    return Math.round(num * factor) / factor;
}

function floorTo(num: number, decimals: number) {
    const factor = 10 ** decimals;
    return Math.floor(num * factor) / factor;
}

export interface IExchangeRatesContextV2 {
    setExchangeRatesData: (val: IExchangeRatesMapResponse) => void;
    exchangeRatesIsReady: boolean;
    exchangeRates: Record<Currency, IExchangeRatePair[]> | null;
    usdExchangeRate: number;
    defaultCurrencyExchangeRate: number;
    fxCurrencyExchangeRate: number;
    exchangeRate: number;
    minBet: number;
    maxBet: number;
    maxWin: number;
}

export const ExchangeRatesContextV2 = createContext<IExchangeRatesContextV2>(null as any);

type IRatesState = {
    data: IExchangeRatesMapResponse | null;
    loading: boolean;
};

type Props = {
    fxCurrency: Currency;
};

export const ExchangeRatesProviderV2: FC<Props & PropsWithChildren> = ({
    children,
    fxCurrency,
}) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const fiatEnabled = COMMON_SITE_CONFIGS.wallet.fiat;

    const { userData } = useContext(UserAccountContextV2);
    const { currentCurrency } = useContext(WalletsContext);
    const {
        siteDefaultCurrency,
        betProcessorMinBet,
        betProcessorMaxBet,
        betProcessorMaxWin,
        sitePaymentsCurrencyConfig,
    } = useContext(ConfigContextV2);
    const [rates, setRates] = useState<IRatesState>({
        data: null,
        loading: false,
    });

    const setExchangeRatesData = useCallback((val: IExchangeRatesMapResponse) => {
        setRates((state) => ({
            ...state,
            data: val,
            loading: false,
        }));
    }, []);

    const exchangeRatesIsReady = useMemo(
        () => Boolean(rates.data?.revision),
        [rates.data?.revision],
    );

    const exchangeRates = useMemo(() => rates.data?.rates ?? null, [rates.data?.rates]);

    const exchangeRateForActiveCurrency = useMemo(
        () => currentCurrency && exchangeRates?.[currentCurrency as Currency],
        [currentCurrency, exchangeRates],
    );

    const usdExchangeRate = useMemo(() => {
        const result = parseFloat(
            exchangeRateForActiveCurrency?.find((item) => item.toCurrency === Currency.USD)?.rate ??
                "",
        );
        return !Number.isNaN(result) ? result : 0;
    }, [exchangeRateForActiveCurrency]);

    const defaultCurrencyExchangeRate = useMemo(() => {
        const result = parseFloat(
            exchangeRateForActiveCurrency?.find((item) => item.toCurrency === siteDefaultCurrency)
                ?.rate ?? "",
        );
        return !Number.isNaN(result) ? result : 0;
    }, [exchangeRateForActiveCurrency, siteDefaultCurrency]);

    const fxCurrencyExchangeRate = useMemo(() => {
        const result = parseFloat(
            exchangeRateForActiveCurrency?.find((item) => item.toCurrency === fxCurrency)?.rate ??
                "",
        );
        return !Number.isNaN(result) ? result : 0;
    }, [exchangeRateForActiveCurrency, fxCurrency]);

    const userCurrencyExchangeRate = useMemo(() => {
        const result = parseFloat(
            exchangeRateForActiveCurrency?.find((item) => item.toCurrency === userData?.currency)
                ?.rate ?? "",
        );

        return !Number.isNaN(result) ? result : 0;
    }, [exchangeRateForActiveCurrency, userData?.currency]);

    const exchangeRate = useMemo(
        () => (fiatEnabled ? userCurrencyExchangeRate : fxCurrencyExchangeRate),
        [fiatEnabled, fxCurrencyExchangeRate, userCurrencyExchangeRate],
    );

    const currentCurrencyDigits = useMemo(
        () => (currentCurrency && sitePaymentsCurrencyConfig?.[currentCurrency]?.digits) ?? null,
        [currentCurrency, sitePaymentsCurrencyConfig],
    );

    const minBet = useMemo(() => {
        const result = (betProcessorMinBet?.amount_usd ?? 0) / usdExchangeRate;

        return !Number.isNaN(parseInt(result.toString())) && currentCurrencyDigits !== null
            ? roundTo(result, currentCurrencyDigits)
            : 0;
    }, [betProcessorMinBet?.amount_usd, usdExchangeRate, currentCurrencyDigits]);

    const maxBet = useMemo(() => {
        const result = (betProcessorMaxBet?.amount_usd ?? 0) / usdExchangeRate;

        return !Number.isNaN(parseInt(result.toString())) && currentCurrencyDigits !== null
            ? floorTo(result, currentCurrencyDigits)
            : 0;
    }, [betProcessorMaxBet?.amount_usd, usdExchangeRate, currentCurrencyDigits]);

    const maxWin = useMemo(() => {
        const result = (betProcessorMaxWin?.amount_usd ?? 0) / usdExchangeRate;

        return !Number.isNaN(parseInt(result.toString())) && currentCurrencyDigits !== null
            ? floorTo(result, currentCurrencyDigits)
            : 0;
    }, [betProcessorMaxWin?.amount_usd, usdExchangeRate, currentCurrencyDigits]);

    const value = useMemo(
        () => ({
            setExchangeRatesData,
            exchangeRatesIsReady,
            exchangeRates,
            usdExchangeRate,
            defaultCurrencyExchangeRate,
            fxCurrencyExchangeRate,
            minBet,
            maxBet,
            maxWin,
            exchangeRate,
        }),
        [
            setExchangeRatesData,
            exchangeRatesIsReady,
            exchangeRates,
            usdExchangeRate,
            defaultCurrencyExchangeRate,
            fxCurrencyExchangeRate,
            minBet,
            maxBet,
            maxWin,
            exchangeRate,
        ],
    );

    return (
        <ExchangeRatesContextV2.Provider value={value}>{children}</ExchangeRatesContextV2.Provider>
    );
};
